import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

export interface ScanCreditsState {
  isPro: boolean
  scansCount: number
  loading: boolean
  canScan: boolean
  refetchCredits: () => Promise<void>
  verifyCanScan: () => Promise<{ allowed: boolean; reason?: string }>
  refetchError: string | null
}

export function useScanCredits(): ScanCreditsState {
  const { user } = useAuth()
  const [isPro, setIsPro] = useState(false)
  const [scansCount, setScansCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [refetchError, setRefetchError] = useState<string | null>(null)

  // Core function to fetch credits from backend - REPLACES all local state
  const fetchCreditsFromBackend = useCallback(async (): Promise<{
    isPro: boolean
    scansCount: number
  } | null> => {
    if (!user?.id) {
      console.log('[useScanCredits] No user ID, cannot fetch credits')
      return null
    }

    try {
      console.log('[useScanCredits] Fetching credits from backend...')
      const { data, error } = await supabase
        .from('profiles')
        .select('is_pro, scan_credits')
        .eq('id', user.id)
        .single()

      if (error) {
        console.error('[useScanCredits] Backend fetch error:', error)
        throw error
      }

      console.log('[useScanCredits] Backend returned:', {
        is_pro: data?.is_pro,
        scan_credits: data?.scan_credits,
      })

      return {
        isPro: data?.is_pro || false,
        scansCount: data?.scan_credits || 0,
      }
    } catch (err) {
      console.error('[useScanCredits] Error fetching from backend:', err)
      throw err
    }
  }, [user?.id])

  // Initial fetch on mount
  useEffect(() => {
    if (!user?.id) {
      setLoading(false)
      return
    }

    const initialFetch = async () => {
      try {
        setLoading(true)
        setRefetchError(null)
        const result = await fetchCreditsFromBackend()
        if (result) {
          setIsPro(result.isPro)
          setScansCount(result.scansCount)
        }
      } catch (err) {
        console.error('[useScanCredits] Initial fetch failed:', err)
        setRefetchError('Failed to load credits')
        // Default to blocking scans on error
        setIsPro(false)
        setScansCount(0)
      } finally {
        setLoading(false)
      }
    }

    initialFetch()

    // Real-time subscription for profile changes - REPLACES local state with backend values
    const channel = supabase
      .channel('profile-credits-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`,
        },
        (payload) => {
          const newProfile = payload.new as {
            is_pro: boolean
            scan_credits: number
          }
          console.log(
            '[useScanCredits] Realtime update - REPLACING local state with backend values:',
            newProfile,
          )
          // CRITICAL: Replace local state entirely with backend values
          setIsPro(newProfile.is_pro || false)
          setScansCount(newProfile.scan_credits || 0)
          setRefetchError(null) // Clear any previous errors
        },
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user?.id, fetchCreditsFromBackend])

  /**
   * CRITICAL: Re-fetch credits from backend after ANY scan event
   * This MUST be called after scan completion (success or failure)
   * The returned values REPLACE all local state
   */
  const refetchCredits = useCallback(async (): Promise<void> => {
    console.log('[useScanCredits] refetchCredits called - querying backend...')
    setRefetchError(null)

    try {
      const result = await fetchCreditsFromBackend()
      if (result) {
        console.log(
          '[useScanCredits] Replacing local state with backend values:',
          result,
        )
        setIsPro(result.isPro)
        setScansCount(result.scansCount)
      }
    } catch (err: any) {
      console.error('[useScanCredits] refetchCredits failed:', err)
      setRefetchError('Failed to refresh credits')
      // CRITICAL: On failure, default to blocking scans (fail-safe)
      setIsPro(false)
      setScansCount(0)
    }
  }, [fetchCreditsFromBackend])

  /**
   * CRITICAL: Verify with backend before allowing scan initiation
   * Returns { allowed: true } only if backend confirms credits > 0 or user is pro
   * This prevents race conditions and stale state issues
   */
  const verifyCanScan = useCallback(async (): Promise<{
    allowed: boolean
    reason?: string
  }> => {
    if (!user?.id) {
      return { allowed: false, reason: 'NOT_LOGGED_IN' }
    }

    console.log('[useScanCredits] verifyCanScan - checking backend...')

    try {
      const result = await fetchCreditsFromBackend()

      if (!result) {
        return { allowed: false, reason: 'FETCH_FAILED' }
      }

      // Update local state with fresh backend values
      setIsPro(result.isPro)
      setScansCount(result.scansCount)
      setRefetchError(null)

      // Check if scan is allowed based on backend values
      if (result.isPro) {
        console.log('[useScanCredits] verifyCanScan: ALLOWED (Pro user)')
        return { allowed: true }
      }

      if (result.scansCount > 0) {
        console.log(
          '[useScanCredits] verifyCanScan: ALLOWED (credits:',
          result.scansCount,
          ')',
        )
        return { allowed: true }
      }

      console.log('[useScanCredits] verifyCanScan: BLOCKED (no credits)')
      return { allowed: false, reason: 'NO_CREDITS' }
    } catch (err: any) {
      console.error('[useScanCredits] verifyCanScan failed:', err)
      setRefetchError('Failed to verify credits')
      // CRITICAL: On failure, default to blocking scans (fail-safe)
      return { allowed: false, reason: 'VERIFICATION_FAILED' }
    }
  }, [user?.id, fetchCreditsFromBackend])

  // Compute canScan from current state (but always verify with backend before actual scan)
  const canScan = isPro || scansCount > 0

  return {
    isPro,
    scansCount,
    loading,
    canScan,
    refetchCredits,
    verifyCanScan,
    refetchError,
  }
}
